/**
 * Pure visibility logic for the BrokerTokenGateModal.
 *
 * Kept framework-free so the decision can be unit-tested in isolation.
 * The modal must never render on the Broker Settings page (/broker), where
 * the user actually generates the session token — otherwise it covers the
 * very page they were sent to.
 */

export function isBrokerSettingsRoute(pathname) {
  const p = (pathname || '').replace(/\/+$/, '');
  return p === '/broker';
}

/**
 * Positively established paid eligibility from the caller's own
 * `GET /billing/subscription` payload (the latest subscription row). Mirrors the
 * backend's `SubscriptionService.is_paid_user()`: ACTIVE, not expired, and not a
 * TRIAL. Anything missing, malformed or belonging to another user is not paid.
 * Presentation only; the backend stays authoritative for broker mapping.
 */
export function isActivePaidSubscription(subscription, { userId = null, now = Date.now() } = {}) {
  if (!subscription || typeof subscription !== 'object') return false;
  if (String(subscription.status || '').toUpperCase() !== 'ACTIVE') return false;
  const source = String(subscription.source || '').toUpperCase();
  if (!source || source === 'TRIAL') return false;
  const validTill = Date.parse(subscription.valid_till);
  if (!Number.isFinite(validTill) || validTill < now) return false;
  if (userId != null && subscription.user_id != null && String(subscription.user_id) !== String(userId)) {
    return false;
  }
  return true;
}

/**
 * Whether the automatic post-login broker reminder may be considered at all.
 *
 * End users must hold a positively loaded active paid subscription, because
 * broker mapping is a paid feature: loading, failed or unknown eligibility
 * never shows the reminder. Operators need no personal subscription here; which
 * Operator reminders appear is decided in `computeBrokerGate` (admins are
 * excluded earlier by `skipGate`). This does not affect the explicit
 * LIVE-deploy broker prompt, which is driven separately.
 */
export function isAutomaticBrokerReminderEligible({
  role,
  subscription,
  subscriptionLoaded = false,
  userId = null,
  now = Date.now(),
}) {
  if (role === 'operator') return true;
  if (subscriptionLoaded !== true) return false;
  return isActivePaidSubscription(subscription, { userId, now });
}

export function computeBrokerGate({ skipGate, reminderEligible, role, status, tokenDismissed, mapDismissed, pathname }) {
  if (skipGate || reminderEligible !== true || !status) return { showGate: false, gateReason: null };
  if (isBrokerSettingsRoute(pathname)) return { showGate: false, gateReason: null };

  const tokenStatus = status.token_status || 'NO_BROKER';
  if (!status.has_active_broker) {
    // Personal broker mapping is optional for an Operator, so logging in never
    // prompts one to connect a broker. An Operator who has mapped one still
    // gets the session reminder below.
    if (role === 'operator') return { showGate: false, gateReason: null };
    return mapDismissed
      ? { showGate: false, gateReason: null }
      : { showGate: true, gateReason: 'no_broker' };
  }
  if ((tokenStatus === 'EXPIRED' || tokenStatus === 'NOT_SET') && !tokenDismissed) {
    return { showGate: true, gateReason: 'token_expired' };
  }
  return { showGate: false, gateReason: null };
}
